/* eslint-disable react/jsx-no-useless-fragment */
import { BoxComponent } from '../../../../../base/box/Box';
import { memo, useEffect, useState } from 'react';
import Item from '../Item';
import {
  //  extractFontFamilyFromUrl,
  getLastItemIndexOfCategory,
  getScaleSize,
  getTemplate6Items,
  isBetweenRange,
} from '../../../../../utils/utils';
import { MenuCategoriesProps, Template } from '@zeus/shared';
import { TypographyComponent } from '../../../../../base/typography/Typography';
//import { GridComponent } from '../../../../../base/grid/Grid';
import { TEMPLATE_6_MODIFIERS } from '../../../../../utils/consts';
import { useNormalizedPixels } from '../../../../../hooks/useResize';
import { Grid as GridComponent } from '../../../../../base19/grid/Grid';

interface ICategory extends MenuCategoriesProps {
  isActive?: boolean;
}

interface CategoryProps {
  template: Omit<Template, 'content'>;
  category: ICategory | null;
  development?: boolean;
  timezone?: string;
  scale?: number;
  categoryIndex: number;
  tenantId?: number;
  lastCategoryIndex?: number;
  fontFamily?: string;
}

const Category = ({
  template,
  development,
  category,
  timezone,
  categoryIndex,
  scale = 1,
  tenantId,
  lastCategoryIndex,
  fontFamily: fontFamilyProp,
}: CategoryProps) => {
  const [active, setActive] = useState<boolean | null>(true);
  const { normalizePixels } = useNormalizedPixels();

  useEffect(() => {
    if (development === false) {
      const intervalId = setInterval(() => {
        setActive(isBetweenRange(category?.schedules, timezone));
      }, 10000);

      return () => clearInterval(intervalId);
    }
  }, []);

  const sortedItems = getTemplate6Items(category, template?.itemsLimit, TEMPLATE_6_MODIFIERS, timezone, development);
  const hasAnyModifier = sortedItems.some((item) => item.hasValidModifiers);
  const isDevelopment = development ? true : isBetweenRange(category?.schedules, timezone);
  const lastItemIndex = getLastItemIndexOfCategory(sortedItems) ?? -1;
  const isCategoryEnabled = template?.category?.isEnabled ?? true;
  const hasBrandLogo = template?.brandLogo && template?.brandLogo?.isEnabled && template?.brandLogo?.image;
  // const fontFamily = fontFamilyProp || extractFontFamilyFromUrl(template?.fontFamily);
  const fontFamily = template?.fontFamilyName;

  return (
    active && (
      <>
        {isDevelopment && (
          <BoxComponent
            component="section"
            sx={{
              position: 'relative',
              ...(category?.isActive && categoryIndex !== lastCategoryIndex && isCategoryEnabled
                ? { marginBottom: getScaleSize(normalizePixels(20, 'height'), scale) }
                : {}),
            }}
          >
            {/* Category */}
            {isCategoryEnabled && (
              <BoxComponent
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: getScaleSize(normalizePixels(14, 'height'), scale),
                  ...(category?.isActive && categoryIndex === 0
                    ? { minHeight: getScaleSize(normalizePixels(90, 'height'), scale) }
                    : {}),
                }}
              >
                <TypographyComponent
                  component={'h1'}
                  sx={{
                    color: template?.category?.color ? template?.category?.color : '#242528',
                    fontSize: getScaleSize(
                      normalizePixels(70 * Number(template?.category?.textScale ?? 1), 'height'),
                      scale
                    ),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    '-webkit-line-clamp': '1',
                    '-webkit-box-orient': 'vertical',
                    fontFamily: fontFamily,
                    fontWeight: template.category?.fontWeight ?? '700',
                    fontStyle: template.category?.fontStyle,
                    letterSpacing: template.category?.letterSpacing,
                    lineHeight: template.category?.lineHeight,
                  }}
                >
                  {category?.name}
                </TypographyComponent>

                {hasBrandLogo && category?.isActive && categoryIndex === 0 && (
                  <div style={{ height: getScaleSize(normalizePixels(70, 'height'), scale) }}>
                    <img
                      src={template?.brandLogo?.image as string}
                      alt="Brand logo"
                      height={'100%'}
                      width={'100%'}
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                )}
              </BoxComponent>
            )}

            {/* Modifier options */}
            {(hasAnyModifier || sortedItems.length === 0) && template?.itemPrice?.isEnabled && (
              <BoxComponent
                component="div"
                display="flex"
                justifyContent="flex-end"
                my={getScaleSize(normalizePixels(10, 'height'), scale)}
              >
                <BoxComponent width={'48%'}>
                  <GridComponent container>
                    {['S', 'M', 'L'].map((val) => (
                      <GridComponent key={val} item size={4}>
                        <TypographyComponent
                          sx={{
                            color: template?.item?.itemName?.color ? template.item.itemName.color : '#242528',
                            fontWeight: '400',
                            textAlign: 'center',
                            fontFamily: fontFamily,
                            lineHeight: 'normal',
                            fontSize: getScaleSize(normalizePixels(30, 'height'), scale),
                          }}
                        >
                          {val}
                        </TypographyComponent>
                      </GridComponent>
                    ))}
                  </GridComponent>
                </BoxComponent>
              </BoxComponent>
            )}

            <BoxComponent component={'div'}>
              <BoxComponent
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                {sortedItems.slice(0, template?.itemsLimit ?? 13).map((item, index) => (
                  <Item
                    key={`${item.id}-${active}`}
                    item={item}
                    template={template}
                    development={development}
                    index={index}
                    lastItemIndex={lastItemIndex}
                    timezone={timezone}
                    scale={scale}
                    hasAnyModifier={hasAnyModifier}
                    tenantId={tenantId}
                    fontFamily={fontFamily}
                    categoryId={category?.id || 0}
                  />
                ))}
              </BoxComponent>
            </BoxComponent>
          </BoxComponent>
        )}
      </>
    )
  );
};

export default memo(Category);
